import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ChartBarIcon } from '@heroicons/react/24/outline'
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Heading, LoadingSpinner, Select, SortableTableHeader, Text } from '../../components/ui'
import { CheckboxMultiSelect } from '../Qualidade/CheckboxMultiSelect'
import { formsService } from '../../services/formsService'
import type {
  Form,
  FormField,
  FormFieldTipo,
  FormResponseRelatorioFiltros,
  FormResponseRelatorioRow,
} from '../../types/forms'
import {
  ContatosPortalCorretorFiltroInput,
  ContatosPortalFiltrosSection,
  ContatosPortalPeriodoFiltroInput,
  ContatosPortalResponsavelFiltroInput,
  FiltroField,
  contatosPortalFiltroFieldClass,
} from './ContatosPortalFiltros'
import ContatosPortalFormularioRespostaDetalheModal from './ContatosPortalFormularioRespostaDetalheModal'
import { ContatosPortalTableSection } from './ContatosPortalTableSection'
import { useContatosPortalFiltros } from './useContatosPortalFiltros'

const PAGE_SIZE = 20
const CHART_HEIGHT = 300
const CHART_LABEL_MAX = 18
const CHART_COLOR = '#008B95'
const CHARTABLE_TIPOS: ReadonlySet<FormFieldTipo> = new Set([
  'number',
  'date',
  'select',
  'radio',
  'checkbox',
])

const tooltipStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--border-color)',
  borderRadius: '0.5rem',
}

type ChartPoint = {
  name: string
  nameFull: string
  quantidade: number
}

function truncateChartLabel(label: string, max = CHART_LABEL_MAX): string {
  if (label.length <= max) return label
  return `${label.slice(0, max - 1)}…`
}

function ChartTooltipContent({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ value?: number | string }>
  label?: string | number
}) {
  if (!active || !payload?.length) return null
  return (
    <div
      className="rounded-lg px-3 py-2 text-sm shadow-lg max-w-sm"
      style={tooltipStyle}
    >
      <div className="font-semibold text-gray-900 dark:text-gray-50 break-words">
        {String(label ?? '')}
      </div>
      <div className="mt-1 text-gray-700 dark:text-gray-200">
        Quantidade: {Number(payload[0]?.value ?? 0)}
      </div>
    </div>
  )
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Não foi possível carregar as respostas dos formulários.'
}

function unionSchemaFields(rows: FormResponseRelatorioRow[], fallback: FormField[]): FormField[] {
  const byId = new Map<number, FormField>()
  const sorted = [...rows].sort((a, b) => b.form_version - a.form_version)
  for (const row of sorted) {
    for (const field of row.schema) {
      if (!byId.has(field.id)) byId.set(field.id, field)
    }
  }
  if (byId.size === 0) {
    for (const field of fallback) {
      if (!byId.has(field.id)) byId.set(field.id, field)
    }
  }
  return Array.from(byId.values())
}

function formatResponseValue(value: unknown, field?: FormField): string {
  if (value == null || value === '') return '—'
  const options = field?.opcoes
  if (Array.isArray(value)) {
    const labels = value.map((item) => {
      const raw = String(item)
      return options?.find((opt) => opt.valor === raw)?.label ?? raw
    })
    return labels.filter(Boolean).join(', ') || '—'
  }
  const raw = String(value)
  return options?.find((opt) => opt.valor === raw)?.label ?? raw
}

function parseNumericValue(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string' || !value.trim()) return null
  const n = Number(value.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

function formatDateTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('pt-BR')
}

type RespostaSortKey = 'created_at' | 'broker_name' | 'form_version'

function isChartableField(field: FormField): boolean {
  return CHARTABLE_TIPOS.has(field.tipo)
}

function normalizeChartLabel(label: string): string {
  return label.trim().replace(/\s+/g, ' ') || '—'
}

function resolveOptionLabel(raw: string, field: FormField): string {
  return field.opcoes?.find((opt) => opt.valor === raw)?.label ?? raw
}

function parseChartDate(value: unknown): { key: string; label: string } | null {
  if (value == null || value === '') return null
  const raw = String(value).trim()
  if (!raw) return null
  const datePart = raw.slice(0, 10)
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(datePart)
    ? new Date(`${datePart}T12:00:00`)
    : new Date(raw)
  if (Number.isNaN(parsed.getTime())) {
    const fallback = normalizeChartLabel(raw)
    return { key: fallback, label: fallback }
  }
  const key = /^\d{4}-\d{2}-\d{2}$/.test(datePart)
    ? datePart
    : `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`
  return { key, label: parsed.toLocaleDateString('pt-BR') }
}

type ChartBuild = {
  data: ChartPoint[]
}

function bumpCount(
  counts: Map<string, { label: string; count: number; sortKey: string }>,
  key: string,
  label: string,
  sortKey: string
) {
  const current = counts.get(key) ?? { label, count: 0, sortKey }
  current.count += 1
  counts.set(key, current)
}

function buildChartData(rows: FormResponseRelatorioRow[], field: FormField | undefined): ChartBuild {
  if (!field || !isChartableField(field) || rows.length === 0) {
    return { data: [] }
  }

  const counts = new Map<string, { label: string; count: number; sortKey: string }>()
  const fieldKey = String(field.id)

  for (const row of rows) {
    const value = row.responses[fieldKey]

    if (field.tipo === 'checkbox') {
      const items = Array.isArray(value)
        ? value
        : value != null && value !== ''
          ? [value]
          : []
      for (const item of items) {
        const label = normalizeChartLabel(resolveOptionLabel(String(item), field))
        if (label === '—') continue
        bumpCount(counts, label, label, label)
      }
      continue
    }

    if (field.tipo === 'date') {
      const parsed = parseChartDate(value)
      if (!parsed) continue
      bumpCount(counts, parsed.key, parsed.label, parsed.key)
      continue
    }

    if (field.tipo === 'number') {
      const n = parseNumericValue(value)
      if (n == null) continue
      const label = String(n)
      bumpCount(counts, label, label, label)
      continue
    }

    if (value == null || value === '') continue
    const raw = Array.isArray(value) ? value[0] : value
    if (raw == null || raw === '') continue
    const label = normalizeChartLabel(resolveOptionLabel(String(raw), field))
    if (label === '—') continue
    bumpCount(counts, label, label, label)
  }

  const entries = Array.from(counts.values())
  if (field.tipo === 'date') {
    entries.sort((a, b) => a.sortKey.localeCompare(b.sortKey))
  } else {
    entries.sort(
      (a, b) => b.count - a.count || a.label.localeCompare(b.label, 'pt-BR')
    )
  }

  return {
    data: entries.map(({ label, count }) => ({
      name: label,
      nameFull: label,
      quantidade: count,
    })),
  }
}

const ContatosPortalFormulariosRespostasTab: React.FC = () => {
  const {
    isComercialFilterRestricted,
    loadingComercialUsers,
    comerciaisOptions,
    brokerId,
    selectedBroker,
    setCorretor,
    setComercialUserId,
    periodo,
    setPeriodo,
    periodoInvalido,
    setPeriodoInvalido,
    displayedComercialUserId,
    effectiveComercialUserId,
    validatePeriodo,
    resetFiltrosBase,
  } = useContatosPortalFiltros()

  const [formGroups, setFormGroups] = useState<Form[]>([])
  const [activeFormGroupId, setActiveFormGroupId] = useState<string>('')
  const [groupsLoading, setGroupsLoading] = useState(true)
  const [groupsError, setGroupsError] = useState<string | null>(null)

  const [rows, setRows] = useState<FormResponseRelatorioRow[]>([])
  const [chartRows, setChartRows] = useState<FormResponseRelatorioRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emptyMessage, setEmptyMessage] = useState('Nenhuma resposta encontrada para este formulário.')
  const [hasSearched, setHasSearched] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [total, setTotal] = useState(0)

  const [chartFieldId, setChartFieldId] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [sortKey, setSortKey] = useState<RespostaSortKey>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedRow, setSelectedRow] = useState<FormResponseRelatorioRow | null>(null)

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total])
  const categoryOptions = useMemo(() => {
    const names = new Set<string>()
    for (const form of formGroups) {
      for (const category of form.categories ?? []) {
        if (category.trim()) names.add(category.trim())
      }
    }
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((name) => ({ value: name, label: name }))
  }, [formGroups])

  const visibleFormGroups = useMemo(() => {
    return formGroups.filter((form) => {
      if (statusFilter === 'active' && !form.is_active) return false
      if (statusFilter === 'inactive' && form.is_active) return false
      if (selectedCategories.length > 0) {
        const formCategories = form.categories ?? []
        const matches = selectedCategories.some((category) => formCategories.includes(category))
        if (!matches) return false
      }
      return true
    })
  }, [formGroups, selectedCategories, statusFilter])
  const selectedForm = useMemo(
    () => visibleFormGroups.find((form) => form.form_group_id === activeFormGroupId) ?? null,
    [visibleFormGroups, activeFormGroupId]
  )

  const schemaFields = useMemo(
    () => unionSchemaFields(chartRows.length ? chartRows : rows, selectedForm?.schema ?? []),
    [chartRows, rows, selectedForm]
  )
  const chartableFields = useMemo(() => schemaFields.filter(isChartableField), [schemaFields])

  useEffect(() => {
    if (!chartableFields.length) {
      setChartFieldId('')
      return
    }
    const ids = new Set(chartableFields.map((field) => String(field.id)))
    setChartFieldId((current) =>
      current && ids.has(current) ? current : String(chartableFields[0].id)
    )
  }, [chartableFields])

  useEffect(() => {
    if (!visibleFormGroups.length) {
      if (activeFormGroupId) setActiveFormGroupId('')
      return
    }
    if (!visibleFormGroups.some((form) => form.form_group_id === activeFormGroupId)) {
      setActiveFormGroupId(visibleFormGroups[0].form_group_id)
    }
  }, [visibleFormGroups, activeFormGroupId])

  const chartField = chartableFields.find((field) => String(field.id) === chartFieldId)
  const chart = useMemo(() => buildChartData(chartRows, chartField), [chartRows, chartField])

  const sortedRows = useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      switch (sortKey) {
        case 'created_at':
          av = a.created_at
          bv = b.created_at
          break
        case 'broker_name':
          av = a.broker_name.toLowerCase()
          bv = b.broker_name.toLowerCase()
          break
        case 'form_version':
          av = a.form_version
          bv = b.form_version
          break
      }
      if (av < bv) return sortOrder === 'asc' ? -1 : 1
      if (av > bv) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
    return copy
  }, [rows, sortKey, sortOrder])

  const handleColumnSort = useCallback((key: string) => {
    const nextKey = key as RespostaSortKey
    if (sortKey === nextKey) {
      setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(nextKey)
    setSortOrder(nextKey === 'created_at' ? 'desc' : 'asc')
  }, [sortKey])

  const detailFields = useMemo(
    () => (selectedRow ? unionSchemaFields([selectedRow], schemaFields) : schemaFields),
    [selectedRow, schemaFields]
  )

  useEffect(() => {
    let cancelled = false
    const loadGroups = async () => {
      setGroupsLoading(true)
      setGroupsError(null)
      try {
        const groups = await formsService.listFormGroupsForRelatorio()
        if (cancelled) return
        setFormGroups(groups)
        setActiveFormGroupId((current) => current || groups[0]?.form_group_id || '')
      } catch (err) {
        if (cancelled) return
        setGroupsError(getErrorMessage(err))
        setFormGroups([])
      } finally {
        if (!cancelled) setGroupsLoading(false)
      }
    }
    void loadGroups()
    return () => {
      cancelled = true
    }
  }, [])

  const carregar = useCallback(
    async (page = 1, formGroupId = activeFormGroupId) => {
      if (!formGroupId) {
        setError('Nenhum formulário disponível.')
        setHasSearched(true)
        return
      }

      setLoading(true)
      setError(null)
      try {
        if (effectiveComercialUserId === '\0') {
          setError('Não foi possível identificar o usuário logado para filtrar as respostas.')
          setRows([])
          setChartRows([])
          setTotal(0)
          setHasSearched(true)
          return
        }

        const periodoErro = validatePeriodo()
        if (periodoErro) {
          setPeriodoInvalido(true)
          setError(periodoErro)
          setRows([])
          setChartRows([])
          setTotal(0)
          setHasSearched(true)
          return
        }
        setPeriodoInvalido(false)

        const filtros: FormResponseRelatorioFiltros = {
          form_group_id: formGroupId,
          data_inicio: periodo.dataInicio,
          data_fim: periodo.dataFim,
        }
        if (brokerId) filtros.broker_id = brokerId
        if (effectiveComercialUserId) filtros.comercial_user_id = effectiveComercialUserId

        const [tabela, grafico] = await Promise.all([
          formsService.listResponsesForRelatorio(filtros, page, PAGE_SIZE),
          formsService.listResponsesForChart(filtros),
        ])
        setRows(tabela.data)
        setTotal(tabela.total)
        setCurrentPage(tabela.page)
        setChartRows(grafico)
        setEmptyMessage(
          tabela.empty_reason === 'comercial_sem_corretoras'
            ? 'Nenhum corretor vinculado a este comercial responsável.'
            : 'Nenhuma resposta encontrada para este formulário.'
        )
        setHasSearched(true)
      } catch (err) {
        console.error('[ContatosPortalFormulariosRespostasTab]', err)
        setError(getErrorMessage(err))
        setRows([])
        setChartRows([])
        setTotal(0)
        setHasSearched(true)
      } finally {
        setLoading(false)
      }
    },
    [
      activeFormGroupId,
      brokerId,
      effectiveComercialUserId,
      periodo.dataFim,
      periodo.dataInicio,
      setPeriodoInvalido,
      validatePeriodo,
    ]
  )

  const handleBuscar = () => {
    setCurrentPage(1)
    void carregar(1)
  }

  const handleLimparFiltros = () => {
    resetFiltrosBase()
    setStatusFilter('all')
    setSelectedCategories([])
    setError(null)
    setEmptyMessage('Nenhuma resposta encontrada para este formulário.')
    setHasSearched(false)
    setRows([])
    setChartRows([])
    setTotal(0)
    setCurrentPage(1)
    setSelectedRow(null)
  }

  const handlePageChange = (page: number) => {
    if (loading) return
    void carregar(page)
  }

  const handleChangeFormGroup = (formGroupId: string) => {
    setActiveFormGroupId(formGroupId)
    setSelectedRow(null)
    if (!hasSearched) return
    setCurrentPage(1)
    void carregar(1, formGroupId)
  }

  const subTabClass = (active: boolean) =>
    `py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${
      active
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  return (
    <div className="flex-1 flex flex-col">
      <ContatosPortalFiltrosSection onBuscar={handleBuscar} onLimpar={handleLimparFiltros} loading={loading}>
        <FiltroField label="Comercial responsável">
          <ContatosPortalResponsavelFiltroInput
            value={displayedComercialUserId}
            onChange={setComercialUserId}
            usuarios={comerciaisOptions}
            disabled={loadingComercialUsers || isComercialFilterRestricted}
            restricted={isComercialFilterRestricted}
          />
        </FiltroField>
        <FiltroField label="Corretor">
          <ContatosPortalCorretorFiltroInput
            value={brokerId}
            initialBroker={selectedBroker}
            onChange={setCorretor}
          />
        </FiltroField>
        <FiltroField label="Status">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            className={contatosPortalFiltroFieldClass}
          >
            <option value="all">Todos</option>
            <option value="active">Ativo</option>
            <option value="inactive">Inativo</option>
          </Select>
        </FiltroField>
        <FiltroField label="Categorias">
          <CheckboxMultiSelect
            options={categoryOptions}
            selected={selectedCategories}
            onChange={setSelectedCategories}
            placeholder="Todas"
            className={contatosPortalFiltroFieldClass}
          />
        </FiltroField>
        <ContatosPortalPeriodoFiltroInput
          layout="grid"
          value={periodo}
          invalid={periodoInvalido}
          onChange={(next) => {
            setPeriodo(next)
            setPeriodoInvalido(false)
          }}
        />
      </ContatosPortalFiltrosSection>

      {groupsLoading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" className="mx-auto" />
        </div>
      ) : groupsError ? (
        <div className="px-6 pb-6">
          <Text className="text-red-600 dark:text-red-400">{groupsError}</Text>
        </div>
      ) : visibleFormGroups.length === 0 ? (
        <div className="px-6 pb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <Text color="muted">
              {formGroups.length === 0
                ? 'Nenhum formulário cadastrado.'
                : 'Nenhum formulário com os filtros selecionados.'}
            </Text>
          </div>
        </div>
      ) : (
        <>
          <div className="px-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <nav className="flex space-x-2 overflow-x-auto" aria-label="Formulários">
              {visibleFormGroups.map((form) => (
                <button
                  key={form.form_group_id}
                  type="button"
                  className={subTabClass(activeFormGroupId === form.form_group_id)}
                  onClick={() => handleChangeFormGroup(form.form_group_id)}
                >
                  {form.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="px-6 py-4">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <ChartBarIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Distribuição das respostas
                  </Heading>
                </div>
                <Text size="sm" color="muted">
                  Escolha um campo para ver a distribuição das respostas.
                </Text>
              </div>

              {chartableFields.length === 0 ? (
                <div className="flex items-center justify-center py-16">
                  <Text color="muted">
                    Este formulário não possui campos adequados para gráfico (apenas texto livre).
                  </Text>
                </div>
              ) : (
                <>
                  <div className="mb-4 max-w-xl">
                    <FiltroField label="Campo">
                      <Select
                        value={chartFieldId}
                        onChange={(e) => setChartFieldId(e.target.value)}
                        className={contatosPortalFiltroFieldClass}
                      >
                        {chartableFields.map((field) => (
                          <option key={field.id} value={String(field.id)}>
                            {field.label}
                          </option>
                        ))}
                      </Select>
                    </FiltroField>
                  </div>

                  {!hasSearched ? (
                    <div className="flex items-center justify-center py-16">
                      <Text color="muted">Use os filtros e clique em Buscar para montar o gráfico.</Text>
                    </div>
                  ) : loading ? (
                    <div className="flex items-center justify-center py-16">
                      <LoadingSpinner size="lg" className="mx-auto" />
                    </div>
                  ) : chart.data.length === 0 ? (
                    <div className="flex items-center justify-center py-16">
                      <Text color="muted">
                        Não há dados suficientes para o gráfico com o campo selecionado.
                      </Text>
                    </div>
                  ) : (
                    <div className="w-full min-w-0 text-gray-600 dark:text-gray-300 [&_.recharts-cartesian-axis-tick-value]:fill-current">
                      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                        <BarChart
                          data={chart.data}
                          margin={{ top: 24, right: 16, left: 4, bottom: 8 }}
                          barCategoryGap="18%"
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#9ca3af"
                            strokeOpacity={0.35}
                          />
                          <XAxis
                            dataKey="name"
                            interval={0}
                            angle={-35}
                            textAnchor="end"
                            height={72}
                            tick={{ fill: 'currentColor', fontSize: 11 }}
                            axisLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                            tickLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                            tickFormatter={(value: string) => truncateChartLabel(value)}
                          />
                          <YAxis
                            allowDecimals={false}
                            width={40}
                            tick={{ fill: 'currentColor', fontSize: 11 }}
                            axisLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                            tickLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                          />
                          <Tooltip
                            cursor={{ fill: 'rgba(156, 163, 175, 0.12)' }}
                            content={<ChartTooltipContent />}
                          />
                          <Bar
                            dataKey="quantidade"
                            name="Quantidade"
                            fill={CHART_COLOR}
                            radius={[4, 4, 0, 0]}
                            maxBarSize={72}
                            isAnimationActive={false}
                          >
                            <LabelList
                              dataKey="quantidade"
                              position="top"
                              offset={6}
                              fill={CHART_COLOR}
                              className="dark:[fill:#5eead4]"
                              fontSize={12}
                              fontWeight={600}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <ContatosPortalTableSection
            loading={loading}
            error={error}
            hasSearched={hasSearched}
            isEmpty={rows.length === 0}
            emptyBeforeSearchMessage="Use os filtros e clique em Buscar para ver o histórico de respostas."
            emptyAfterSearchMessage={emptyMessage}
            currentPage={currentPage}
            totalPages={totalPages}
            total={total}
            pageSize={PAGE_SIZE}
            onPageChange={handlePageChange}
            showHorizontalScroll
          >
              <table className="w-full min-w-max divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                  <tr>
                    <SortableTableHeader
                      sortKey="created_at"
                      currentSort={sortKey}
                      sortOrder={sortOrder}
                      onSort={handleColumnSort}
                      compact
                      className="sticky left-0 z-20 w-[9rem] min-w-[9rem] max-w-[9rem] bg-gray-50 dark:bg-gray-700 px-4 py-3 text-left"
                    >
                      Data
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="broker_name"
                      currentSort={sortKey}
                      sortOrder={sortOrder}
                      onSort={handleColumnSort}
                      compact
                      className="sticky left-[9rem] z-20 w-[14rem] min-w-[14rem] max-w-[14rem] bg-gray-50 px-4 py-3 text-left shadow-[2px_0_0_0_#e5e7eb] dark:bg-gray-700 dark:shadow-[2px_0_0_0_#4b5563]"
                    >
                      Corretor
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="form_version"
                      currentSort={sortKey}
                      sortOrder={sortOrder}
                      onSort={handleColumnSort}
                      compact
                      className="w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] px-4 py-3 text-left"
                    >
                      Versão
                    </SortableTableHeader>
                    {schemaFields.map((field) => (
                      <th
                        key={field.id}
                        title={field.label}
                        className="min-w-[14rem] max-w-[18rem] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 align-bottom"
                      >
                        <span className="block break-words">
                          {field.label}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedRows.map((row) => (
                    <tr
                      key={row.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedRow(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setSelectedRow(row)
                        }
                      }}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset"
                    >
                      <td
                        className="sticky left-0 z-10 w-[9rem] min-w-[9rem] max-w-[9rem] bg-gray-50 px-4 py-4 tabular-nums text-gray-700 align-top dark:bg-gray-700 dark:text-gray-300"
                      >
                        <span className="whitespace-pre-line">
                          {formatDateTime(row.created_at).replace(', ', '\n')}
                        </span>
                      </td>
                      <td
                        className="sticky left-[9rem] z-10 w-[14rem] min-w-[14rem] max-w-[14rem] bg-gray-50 px-4 py-4 text-gray-900 align-top shadow-[2px_0_0_0_#e5e7eb] dark:bg-gray-700 dark:text-white dark:shadow-[2px_0_0_0_#4b5563]"
                      >
                        <span className="font-medium break-words">{row.broker_name}</span>
                        {row.broker_id_scgp != null ? (
                          <span className="block text-xs text-gray-500 dark:text-gray-400">
                            SCGP {row.broker_id_scgp}
                          </span>
                        ) : null}
                      </td>
                      <td
                        className="w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] px-4 py-4 text-left text-gray-700 align-top dark:text-gray-300"
                      >
                        v{row.form_version}
                      </td>
                      {schemaFields.map((field) => (
                        <td
                          key={`${row.id}-${field.id}`}
                          className="min-w-[14rem] max-w-[18rem] px-4 py-4 align-top text-gray-700 dark:text-gray-300"
                        >
                          <span className="whitespace-pre-wrap break-words">
                            {formatResponseValue(row.responses[String(field.id)], field)}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
          </ContatosPortalTableSection>

          <ContatosPortalFormularioRespostaDetalheModal
            row={selectedRow}
            fields={detailFields}
            formatValue={formatResponseValue}
            formatDateTime={formatDateTime}
            onClose={() => setSelectedRow(null)}
          />
        </>
      )}
    </div>
  )
}

export default ContatosPortalFormulariosRespostasTab
